"use client";
import { todayInTimeZone } from "@gettactic/helpers/src/date-time/todayInTimeZone";
import { useLoadingToast } from "@gettactic/helpers/src/hooks/toasts/useLoadingToast";
import {
  useAuthenticated,
  useInvalidateDedicatedExceptions
} from "@gettactic/hooks";
import { format, isValid, parseISO } from "date-fns";
import React from "react";
import type { PickedMember } from "@/features/teams/components/PeopleAndTeamsPicker";
import { api } from "@/lib/api/api";
import { releaseDeskErrorMessage } from "../components/CancelReservations/releaseDeskErrorMessage";

/**
 * What to do with the person's dedicated desk while they are away: keep it
 * (nothing happens), release it to anyone, or release it to the teams and
 * people they name.
 */
export type ReleaseDeskChoice = "keep" | "open" | "assigned";

/** A team or person the desk is released to, with what the chips show. */
export type ReleaseGrantee = PickedMember;

export interface ReleaseDeskSelection {
  choice: ReleaseDeskChoice;
  /** Teams that may book the desk; read only for `assigned`. */
  teams: ReleaseGrantee[];
  /** People who may book the desk; read only for `assigned`. */
  users: ReleaseGrantee[];
}

export const KEEP_DESK_SELECTION: ReleaseDeskSelection = {
  choice: "keep",
  teams: [],
  users: []
};

export const RELEASE_TO_ANYONE_SELECTION: ReleaseDeskSelection = {
  choice: "open",
  teams: [],
  users: []
};

/** A release to specific people needs at least one team or person named. */
export const isReleaseDeskSelectionComplete = (
  selection: ReleaseDeskSelection
): boolean =>
  selection.choice !== "assigned" ||
  selection.teams.length + selection.users.length > 0;

export interface ReleaseDedicatedDeskParams {
  deskId: string;
  deskName: string;
  /**
   * First and last released day, `yyyy-MM-dd`, inclusive. Callers may hand
   * over days read on the viewer's calendar - an agenda day, a date picker -
   * and the release is held to the desk office's own days, so pass
   * `officeId` and let this do that.
   */
  startsOn: string;
  endsOn: string;
  /**
   * The desk's office, so its calendar decides the days. Without it the days
   * are sent as given.
   */
  officeId?: string | null;
  /** Who may book the desk; a `keep` selection releases nothing. */
  selection: ReleaseDeskSelection;
  reason: string;
}

const formatDay = (day: string): string => {
  const parsed = parseISO(`${day}T00:00:00`);
  return isValid(parsed) ? format(parsed, "MMM d") : day;
};

/**
 * Releases the signed-in person's dedicated desk for some days, with the
 * toasts and cache refreshes every caller needs. `release` resolves to
 * whether the desk was released; a failure is already reported.
 */
export const useReleaseDedicatedDesk = () => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const invalidate = useInvalidateDedicatedExceptions();
  const toast = useLoadingToast({ loadingMessage: "Releasing your desk…" });
  const [isReleasing, setIsReleasing] = React.useState(false);

  const release = React.useCallback(
    async (params: ReleaseDedicatedDeskParams): Promise<boolean> => {
      const { selection } = params;
      if (selection.choice === "keep") {
        return false;
      }
      // The released days are the desk office's calendar days. Callers hand
      // over days read on the viewer's - an agenda day keyed on the device
      // zone, a date picker - and across a date line those are a day out:
      // the desk would stay taken today, or the request be refused for
      // starting in the office's past. The office's own zone settles it,
      // when the caller says which office the desk is in.
      const officeToday = params.officeId
        ? todayInTimeZone(
            authenticatedUser.offices?.byId[params.officeId]?.time_zone
          )
        : undefined;
      // Days wholly behind the office release nothing. Raising the start to
      // today and dragging the end up with it would free the desk for today,
      // a day nobody picked.
      if (officeToday && params.endsOn < officeToday) {
        toast.error(
          `Those days have already passed at ${params.deskName}'s office.`
        );
        return false;
      }
      // A span that reaches today or later starts no earlier than today; its
      // end already sits on or after that, so it stands as picked.
      const startsOn =
        officeToday && params.startsOn < officeToday
          ? officeToday
          : params.startsOn;
      const endsOn = params.endsOn;
      setIsReleasing(true);
      toast.startLoading();
      const isAssigned = selection.choice === "assigned";
      let response: Awaited<
        ReturnType<
          typeof api.client.organizations.resourceDedicatedExceptionPost
        >
      >;
      try {
        response =
          await api.client.organizations.resourceDedicatedExceptionPost(
            params.deskId,
            {
              starts_on: startsOn,
              ends_on: endsOn,
              release: isAssigned ? "assigned" : "open",
              teams: isAssigned ? selection.teams.map((t) => t.id) : undefined,
              users: isAssigned ? selection.users.map((u) => u.id) : undefined,
              reason: params.reason
            }
          );
      } catch {
        // A dropped connection rejects rather than answering, and leaving the
        // flag set would disable the prompt that called us for the rest of the
        // page's life.
        toast.error("Couldn't reach the server. Your desk was not released.");
        return false;
      } finally {
        setIsReleasing(false);
      }
      if (!response.ok) {
        toast.error(releaseDeskErrorMessage(response.result));
        return false;
      }
      toast.success(
        startsOn === endsOn
          ? `${params.deskName} is available to others on ${formatDay(startsOn)}.`
          : `${params.deskName} is available to others until ${formatDay(endsOn)}.`
      );
      await invalidate();
      return true;
    },
    [invalidate, toast, authenticatedUser.offices]
  );

  return { release, isReleasing };
};
